import type { ReactElement, Ref, RefCallback } from 'react';

/** Compose event handlers into one that invokes each provided handler in order. */
export const chainHandlers =
  <E>(...handlers: Array<((event: E) => void) | undefined>) =>
  (event: E): void => {
    for (const handler of handlers) {
      handler?.(event);
    }
  };

/**
 * Merge several refs (callback or object) into a single callback ref. Memoise the
 * result when used on an element so the ref isn't detached/reattached every render.
 */
export const mergeRefs =
  <T>(...refs: Array<Ref<T> | undefined>): RefCallback<T> =>
  (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as { current: T | null }).current = node;
      }
    }
  };

/** React 19 exposes a cloned element's ref on `props.ref`. */
export const getElementRef = (
  element: ReactElement,
): Ref<HTMLElement> | undefined =>
  (element.props as { ref?: Ref<HTMLElement> }).ref;
